import { useEffect, useState } from 'react';
import api from '../api';
import { parseBdAssistantCapability } from './bdAssistantCapability';

/**
 * Whether the signed-in user may see the Business Development module
 * (BD-04). Clone of useExternalIntegrations.ts's exact reasoning:
 *
 * The backend is the authority; this only decides whether the UI bothers to
 * render. Hiding the nav entry is a courtesy, not a security control — every
 * BD Assistant endpoint enforces the same flag server-side via
 * requireBdAssistantEligible, so a user who un-hides the page by hand still
 * gets a 403 with an audit row.
 *
 * FAILS CLOSED. A network error, a 401, a malformed body — all resolve to
 * `false`.
 *
 * Cached for the page session for the same reason: the nav renders on every
 * route, and the flag cannot change without an admin action elsewhere, so
 * there's nothing to gain from refetching per-route.
 */

let cached: Promise<boolean> | null = null;

async function fetchCapability(): Promise<boolean> {
  try {
    const res = await api.get('/api/bd-eligibility');
    return parseBdAssistantCapability(res.data);
  } catch {
    return false;
  }
}

/** Drop the cached answer — call after an admin toggles your own eligibility, or on logout. */
export const resetBdAssistantEligibilityCache = (): void => {
  cached = null;
};

export interface BdAssistantEligibilityState {
  /** True only on an explicit yes from the server. */
  allowed: boolean;
  /** True until the first answer arrives. */
  loading: boolean;
}

export function useBdAssistantEligibility(): BdAssistantEligibilityState {
  const [state, setState] = useState<BdAssistantEligibilityState>({
    allowed: false,
    loading: true,
  });

  useEffect(() => {
    let active = true;
    if (!cached) cached = fetchCapability();
    cached.then((allowed) => {
      if (active) setState({ allowed, loading: false });
    });
    return () => {
      active = false;
    };
  }, []);

  return state;
}

export default useBdAssistantEligibility;
